import {
  ArrowClockwise,
  Check,
  FileCsv,
  FileXls,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { suggestMapping, validateParsedImport } from "../domain/importValidation";
import {
  getSourceKind,
  listWorkbookSheets,
  parseListFile,
} from "../services/importService";
import type {
  ImportMapping,
  ParsedImport,
  PickerEntry,
  ValidatedImport,
} from "../types";

export type ImportSource =
  | { kind: "parsed"; parsed: ParsedImport }
  | { kind: "file"; file: File }
  | null;

interface ImportDialogProps {
  source: ImportSource;
  existingEntries: PickerEntry[];
  onClose: () => void;
  onCommit: (
    entries: PickerEntry[],
    mode: "replace" | "append",
    duplicatePolicy: "exclude" | "keep",
  ) => void;
}

function columnName(index: number): string {
  let result = "";
  let value = index;
  do {
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);
  return result;
}

function getColumnCount(rows: string[][]): number {
  return Math.min(20, rows.reduce((maximum, row) => Math.max(maximum, row.length), 0));
}

export function ImportDialog({
  source,
  existingEntries,
  onClose,
  onCommit,
}: ImportDialogProps) {
  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [mapping, setMapping] = useState<ImportMapping>({
    hasHeader: false,
    nameColumn: 0,
    categoryColumn: null,
    weightColumn: null,
  });
  const [sheets, setSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<"replace" | "append">("replace");
  const [duplicatePolicy, setDuplicatePolicy] = useState<"exclude" | "keep">("exclude");

  useEffect(() => {
    if (!source) return;
    let active = true;
    setParsed(null);
    setSheets([]);
    setSelectedSheet("");
    setError("");
    setMode(existingEntries.length > 0 ? "append" : "replace");
    setDuplicatePolicy("exclude");

    const load = async () => {
      if (source.kind === "parsed") {
        setParsed(source.parsed);
        setMapping(suggestMapping(source.parsed.rows));
        return;
      }
      try {
        setLoading(true);
        const sourceKind = getSourceKind(source.file);
        if (sourceKind === "xlsx") {
          const workbookSheets = await listWorkbookSheets(source.file);
          if (!active) return;
          if (workbookSheets.length === 0) throw new Error("This workbook has no worksheets.");
          setSheets(workbookSheets);
          setSelectedSheet(workbookSheets[0] ?? "");
          const result = await parseListFile(source.file, workbookSheets[0]);
          if (!active) return;
          setParsed(result);
          setMapping(suggestMapping(result.rows));
        } else {
          const result = await parseListFile(source.file);
          if (!active) return;
          setParsed(result);
          setMapping(suggestMapping(result.rows));
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : "The file could not be read.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();

    return () => {
      active = false;
    };
  }, [existingEntries.length, source]);

  const validation = useMemo<ValidatedImport | null>(() => {
    if (!parsed) return null;
    return validateParsedImport(parsed, mapping, mode === "append" ? existingEntries : []);
  }, [existingEntries, mapping, mode, parsed]);

  const displayEntries = useMemo(() => {
    if (!validation) return [];
    return duplicatePolicy === "keep"
      ? [...validation.entries, ...validation.duplicates]
      : validation.entries;
  }, [duplicatePolicy, validation]);

  const columnCount = parsed ? getColumnCount(parsed.rows) : 0;
  const columns = Array.from({ length: columnCount }, (_, index) => ({
    value: index,
    label: `${columnName(index)}: ${
      mapping.hasHeader && parsed ? parsed.rows[0]?.[index] || "Untitled" : `Column ${columnName(index)}`
    }`,
  }));

  const changeSheet = async (sheet: string) => {
    if (source?.kind !== "file") return;
    try {
      setSelectedSheet(sheet);
      setLoading(true);
      setError("");
      const result = await parseListFile(source.file, sheet);
      setParsed(result);
      setMapping(suggestMapping(result.rows));
    } catch (sheetError) {
      setError(sheetError instanceof Error ? sheetError.message : "The worksheet could not be read.");
    } finally {
      setLoading(false);
    }
  };

  const confirm = () => {
    if (displayEntries.length === 0) return;
    onCommit(displayEntries, mode, duplicatePolicy);
  };

  return (
    <AnimatePresence>
      {source ? (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="presentation"
        >
          <motion.section
            className="import-dialog"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-title"
          >
            <header className="dialog-header">
              <div className="dialog-title-block">
                <span className="icon-frame" aria-hidden="true">
                  {parsed?.sourceKind === "xlsx" ? <FileXls size={21} /> : <FileCsv size={21} />}
                </span>
                <div>
                  <h2 id="import-title">Review your import</h2>
                  <p>{parsed?.fileName ?? "Pasted list"}</p>
                </div>
              </div>
              <button className="icon-button" type="button" onClick={onClose} aria-label="Close import">
                <X size={20} />
              </button>
            </header>

            {loading ? (
              <div className="import-loading" role="status">
                <span className="loading-bar" />
                <span className="loading-bar short" />
                <span className="loading-bar" />
                <p>Reading the source locally</p>
              </div>
            ) : error ? (
              <div className="dialog-error" role="alert">
                <WarningCircle size={24} />
                <div>
                  <strong>Import could not continue</strong>
                  <p>{error}</p>
                </div>
              </div>
            ) : parsed ? (
              <>
                <div className="import-controls">
                  {sheets.length > 1 ? (
                    <label className="field">
                      <span>Worksheet</span>
                      <select value={selectedSheet} onChange={(event) => void changeSheet(event.target.value)}>
                        {sheets.map((sheet) => (
                          <option key={sheet} value={sheet}>
                            {sheet}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}

                  <label className="check-field">
                    <input
                      type="checkbox"
                      checked={mapping.hasHeader}
                      onChange={(event) =>
                        setMapping((current) => ({ ...current, hasHeader: event.target.checked }))
                      }
                    />
                    <span>First row contains column names</span>
                  </label>

                  <div className="mapping-grid">
                    <label className="field">
                      <span>Name</span>
                      <select
                        value={mapping.nameColumn}
                        onChange={(event) =>
                          setMapping((current) => ({
                            ...current,
                            nameColumn: Number(event.target.value),
                          }))
                        }
                      >
                        {columns.map((column) => (
                          <option key={column.value} value={column.value}>
                            {column.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="field">
                      <span>Category</span>
                      <select
                        value={mapping.categoryColumn ?? ""}
                        onChange={(event) =>
                          setMapping((current) => ({
                            ...current,
                            categoryColumn:
                              event.target.value === "" ? null : Number(event.target.value),
                          }))
                        }
                      >
                        <option value="">Not mapped</option>
                        {columns.map((column) => (
                          <option key={column.value} value={column.value}>
                            {column.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="field">
                      <span>Weight</span>
                      <select
                        value={mapping.weightColumn ?? ""}
                        onChange={(event) =>
                          setMapping((current) => ({
                            ...current,
                            weightColumn:
                              event.target.value === "" ? null : Number(event.target.value),
                          }))
                        }
                      >
                        <option value="">Equal odds</option>
                        {columns.map((column) => (
                          <option key={column.value} value={column.value}>
                            {column.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                <div className="preview-table-wrap">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th key={column.value}>{columnName(column.value)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.slice(0, 7).map((row, rowIndex) => (
                        <tr key={`${rowIndex}-${row.join("|")}`}>
                          {columns.map((column) => (
                            <td key={column.value}>{row[column.value] || <span className="empty-cell">Empty</span>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="import-summary">
                  <div>
                    <strong>{displayEntries.length.toLocaleString()}</strong>
                    <span>ready</span>
                  </div>
                  <div>
                    <strong>{validation?.duplicates.length.toLocaleString() ?? "0"}</strong>
                    <span>duplicates</span>
                  </div>
                  <div>
                    <strong>{validation?.errors.length.toLocaleString() ?? "0"}</strong>
                    <span>invalid</span>
                  </div>
                </div>

                {validation && (validation.duplicates.length > 0 || validation.errors.length > 0) ? (
                  <div className="import-review-note">
                    <WarningCircle size={20} />
                    <div>
                      <strong>Review choices</strong>
                      <p>
                        Duplicate names in the same category are excluded by default. Invalid rows are never imported.
                      </p>
                      {validation.duplicates.length > 0 ? (
                        <label className="check-field compact">
                          <input
                            type="checkbox"
                            checked={duplicatePolicy === "keep"}
                            onChange={(event) =>
                              setDuplicatePolicy(event.target.checked ? "keep" : "exclude")
                            }
                          />
                          <span>Keep duplicates as separate chances</span>
                        </label>
                      ) : null}
                      {validation.errors.slice(0, 3).map((item) => (
                        <p className="row-error" key={`${item.row}-${item.message}`}>
                          Row {item.row}: {item.message}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : null}

                {validation?.warnings.map((warning) => (
                  <p className="dialog-warning" key={warning}>
                    {warning}
                  </p>
                ))}

                <footer className="dialog-footer">
                  <div className="mode-switch" aria-label="Import mode">
                    <button
                      type="button"
                      className={mode === "replace" ? "active" : ""}
                      onClick={() => setMode("replace")}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className={mode === "append" ? "active" : ""}
                      onClick={() => setMode("append")}
                    >
                      Append
                    </button>
                  </div>
                  <button
                    type="button"
                    className="primary-button"
                    onClick={confirm}
                    disabled={displayEntries.length === 0}
                  >
                    <Check size={18} weight="bold" />
                    Import {displayEntries.length.toLocaleString()}
                  </button>
                </footer>
              </>
            ) : (
              <button type="button" className="secondary-button" onClick={onClose}>
                <ArrowClockwise size={18} />
                Close
              </button>
            )}
          </motion.section>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
